/* ---------- Cart shipping bar: fills in the #CartShippingBar shell ----------
   snippets/cart-shipping-progress-bar.liquid renders an empty shell for
   every visitor (id="CartShippingBar"). All the real configuration --
   enable toggle, display style, color, wording, per-country spend zones --
   is rendered once as a JSON blob (id="cart-shipping-bar-data") by
   sections/cart-shipping-zones.liquid, which is part of the normal footer
   render on every full page load. This script:

   1. Reads that JSON blob.
   2. Resolves the visitor's country -- the shell carries data-known-country
      with localization.country's value for every visitor (it's Shopify's
      own resolved country for that session, set either by real
      domain-based market routing -- UK -- or by the customer's own
      explicit pick from the store's country/region selector -- everyone
      else, who share one "International" market; both are equally
      authoritative once set), so that's used straight away with no network
      call. IP-detection via Shopify's own geolocation endpoint
      (browsing_context_suggestions.json -- no third-party API, no
      permission prompt) only ever runs as a fallback, for the rare case
      where a visitor somehow has no resolved country yet -- it must never
      override a country Shopify has already resolved, since that would
      silently ignore a customer's deliberate selection. Cached in
      sessionStorage so it only ever runs once per browser tab.
   3. Matches the country against the zones and fills in the shell --
      message text, display-style class, color, and (in "bar" display
      style) the progress track/fill.

   Why this is JS-driven at all, rather than rendered server-side: this
   shell renders inside the cart drawer, which Shopify's Section Rendering
   API re-renders in total isolation on every cart change (no layout, no
   other sections run in that request). A cross-section Liquid lookup for
   the shipping-zones section's settings from inside the drawer only ever
   resolves on some page loads and never on cart updates -- which is why
   the bar wasn't appearing at all. Reading the JSON blob (rendered once,
   reliably, by the section that owns the data) works in both cases.

   This deliberately never calls Shopify's /localization endpoint or
   otherwise changes the site's active currency/language -- it's scoped to
   just this bar. Runs again on every PUB_SUB_EVENTS.cartUpdate (cart total
   changes, nothing else does), re-using the already-known/cached country.

   Testing: add ?shipping_test_country=AU to any URL to preview a zone
   without needing to actually be there or use a VPN -- it's cached in
   sessionStorage same as a real detection (and takes priority over a
   known/resolved country too), so it sticks for the rest of that browser
   tab while you click around the site. Use ?shipping_test_country= (empty)
   to clear the override and go back to real detection. */

(function () {
  var COUNTRY_CACHE_KEY = 'wlShippingBarCountry';

  function getCountryOverride() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.has('shipping_test_country')) {
        return params.get('shipping_test_country').trim().toUpperCase();
      }
    } catch (e) {
      // malformed URL / URLSearchParams unavailable -- ignore, fall through to real detection
    }
    return null;
  }

  function getContainer() {
    return document.getElementById('CartShippingBar');
  }

  function getData() {
    var el = document.getElementById('cart-shipping-bar-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[cart-shipping-bar] could not parse data JSON', e);
      return null;
    }
  }

  function matchZone(zones, countryCode) {
    var fallback = null;
    for (var i = 0; i < zones.length; i++) {
      var zone = zones[i];
      if (!zone.countries.length) {
        if (!fallback) fallback = zone;
        continue;
      }
      if (countryCode && zone.countries.indexOf(countryCode) !== -1) {
        return zone;
      }
    }
    return fallback;
  }

  function detectCountry(knownCountry) {
    var override = getCountryOverride();
    if (override !== null) {
      try {
        if (override) {
          sessionStorage.setItem(COUNTRY_CACHE_KEY, override);
        } else {
          sessionStorage.removeItem(COUNTRY_CACHE_KEY);
        }
      } catch (e) {
        // sessionStorage unavailable -- override still applies for this page load
      }
      return Promise.resolve(override || null);
    }

    // Server already knows this visitor's real country reliably
    // (localization.country, set via domain-based market routing or the
    // customer's own explicit selector pick) -- skip detection entirely.
    if (knownCountry) {
      return Promise.resolve(knownCountry);
    }

    var cached;
    try {
      cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
    } catch (e) {
      cached = null;
    }
    if (cached) return Promise.resolve(cached);

    if (typeof Shopify === 'undefined' || !window.fetch) {
      return Promise.resolve((typeof Shopify !== 'undefined' && Shopify.country) || null);
    }

    var root = (Shopify.routes && Shopify.routes.root) || '/';
    var url =
      root +
      'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' +
      encodeURIComponent(Shopify.country || '');

    return fetch(url)
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        var code =
          data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
        code = code || Shopify.country || null;
        try {
          if (code) sessionStorage.setItem(COUNTRY_CACHE_KEY, code);
        } catch (e) {
          // sessionStorage unavailable (private browsing etc.) -- fine,
          // detection just re-runs next cart update instead of being cached.
        }
        return code;
      })
      .catch(function () {
        return (typeof Shopify !== 'undefined' && Shopify.country) || null;
      });
  }

  function formatMoney(cents) {
    var currency = (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(navigator.language, { style: 'currency', currency: currency }).format(
        cents / 100
      );
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ---- Small hex/HSL helpers, standing in for Liquid's color_lighten filter
     (increases HSL lightness by a percentage) since the color now needs to
     be applied from JS rather than baked in server-side. ---- */
  function hexToRgb(hex) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
    if (!m) return null;
    return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
  }

  function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    var max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    var h,
      s,
      l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
      }
      h /= 6;
    }
    return { h: h, s: s, l: l };
  }

  function hslToRgb(h, s, l) {
    var r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      var hue2rgb = function (p, q, t) {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      var p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
  }

  function rgbToHex(r, g, b) {
    function toHex(c) {
      var hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }
    return '#' + toHex(r) + toHex(g) + toHex(b);
  }

  function lightenColor(hex, percent) {
    var rgb = hexToRgb(hex);
    if (!rgb) return hex;
    var hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    hsl.l = Math.min(1, hsl.l + percent / 100);
    var out = hslToRgb(hsl.h, hsl.s, hsl.l);
    return rgbToHex(out.r, out.g, out.b);
  }

  function applyStaticData(container, data) {
    var style = data.displayStyle === 'sentence' ? 'sentence' : 'bar';
    container.classList.remove('cart-shipping-bar--bar', 'cart-shipping-bar--sentence');
    container.classList.add('cart-shipping-bar--' + style);

    if (data.color) {
      container.style.setProperty('--shipping-bar-color', data.color);
      container.style.setProperty('--shipping-bar-color-light', lightenColor(data.color, 10));
      container.style.setProperty('--shipping-bar-color-lighter', lightenColor(data.color, 20));
    }
  }

  function render(container, data, zone, cartTotal) {
    if (!zone) {
      container.hidden = true;
      return;
    }

    var threshold = zone.minimumSpend;
    var remaining = threshold - cartTotal;
    var percentage = Math.min(100, Math.max(0, Math.round((cartTotal * 100) / threshold)));
    var reached = remaining <= 0;

    var messageEl = container.querySelector('.cart-shipping-bar__message');
    if (messageEl) {
      var message = reached ? data.reachedMessage : data.message.replace('[amount]', formatMoney(remaining));
      // Only touch the DOM if the text actually changed -- textContent
      // assignment replaces messageEl's child text node, a childList
      // mutation that the #CartDrawer MutationObserver below (subtree:
      // true) would otherwise pick up and use to re-trigger updateFromCart()
      // forever (same trap documented at the top of
      // assets/cart-recommendations.js). Skipping a no-op write breaks that
      // loop after one harmless bounce instead of looping forever.
      if (messageEl.textContent !== message) {
        messageEl.textContent = message;
      }
    }

    var track = container.querySelector('.cart-shipping-bar__track');
    var fill = container.querySelector('.cart-shipping-bar__fill');
    if (track && fill) {
      track.classList.toggle('cart-shipping-bar__track--complete', reached);
      track.setAttribute('aria-valuenow', String(percentage));
      track.setAttribute('aria-label', data.reachedMessage);
      fill.style.width = percentage + '%';
    }

    container.hidden = false;
  }

  function updateFromCart() {
    var container = getContainer();
    if (!container) return;
    var data = getData();
    if (!data || !data.enabled) {
      container.hidden = true;
      return;
    }

    detectCountry(container.dataset.knownCountry || null).then(function (countryCode) {
      // Re-query, and re-read the cart total from it, rather than trusting
      // either the pre-fetch `container` reference or the triggering
      // event's own payload: whatever fired this update (cart.js's
      // /cart/change.js and /cart/update.js, or product-form.js's
      // /cart/add.js for the "you may also like" quick-add buttons) has
      // already caused Shopify's Section Rendering API to swap in a fresh
      // #CartDrawer, replacing the old container entirely -- so the early
      // reference is stale, and applying anything to it (color, the
      // --sentence class) would land on a node that's already been thrown
      // away. Worse, /cart/add.js's response is shaped like the added line
      // item, not the cart, so it has no total_price at all -- reading the
      // freshly-rerendered container's own data-cart-total instead works
      // the same regardless of which endpoint triggered the update.
      var freshContainer = getContainer();
      if (!freshContainer) return;
      var cartTotal = parseInt(freshContainer.dataset.cartTotal, 10) || 0;
      applyStaticData(freshContainer, data);
      var zone = matchZone(data.zones, countryCode);
      render(freshContainer, data, zone, cartTotal);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    // NOTE: deliberately NOT bailing out here just because getContainer()
    // (the #CartShippingBar shell) doesn't exist yet -- snippets/cart-
    // drawer.liquid only renders that shell `unless cart == empty`, so on
    // any page load with an empty cart (the most common case -- e.g.
    // landing straight on a product page) it doesn't exist. An early return
    // here used to skip registering EITHER listener below for the rest of
    // the page's life in that case, so even the very first add-to-cart,
    // which is exactly when the shell first appears, was never picked up.
    // #CartDrawer itself, checked before the MutationObserver is attached
    // below, is the right "does this page even have a cart drawer" guard.
    updateFromCart();

    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      updateFromCart();
    });

    // Primary signal for "add to cart": product-form.js publishes cartUpdate
    // *before* cart-drawer.js's renderContents() swaps in the freshly
    // rendered #CartDrawer (new data-cart-total included, and -- on the
    // very first add to an empty cart -- the #CartShippingBar shell
    // existing at all for the first time) -- so the subscription above
    // fires too early, against the about-to-be-replaced/not-yet-existing
    // shell, which is why the bar was stuck showing 0% right after adding
    // the first item instead of the real percentage. Watching #CartDrawer
    // itself for the actual swap catches the moment the new markup lands,
    // regardless of which code path/timing triggered it -- same pattern as
    // assets/cart-recommendations.js.
    var cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer && window.MutationObserver) {
      var scheduled = false;
      new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          updateFromCart();
        }, 0);
      }).observe(cartDrawer, { childList: true, subtree: true });
    }
  });
})();
